import type { WheelStrainCandidate } from './types.js';

/** THC must be a finite percentage-point value greater than zero. */
export function hasValidWheelThc(
  thcPercent: number | null | undefined,
): thcPercent is number {
  return (
    typeof thcPercent === 'number' &&
    Number.isFinite(thcPercent) &&
    thcPercent > 0 &&
    thcPercent <= 100
  );
}

/**
 * A strain can be safely loaded into the calculator when it has valid THC.
 * (Archive / incompleteness are separate eligibility gates.)
 */
export function canLoadIntoCalculator(strain: WheelStrainCandidate): boolean {
  return hasValidWheelThc(strain.thcPercent);
}

/**
 * Primary wheel pool: active, complete, valid THC, calculator-safe.
 * Deduplicate by id so multiple image/spreadsheet records do not overweight.
 */
export function filterPrimaryWheelEligible(
  strains: readonly WheelStrainCandidate[],
): WheelStrainCandidate[] {
  const seen = new Set<string>();
  const out: WheelStrainCandidate[] = [];
  for (const strain of strains) {
    if (seen.has(strain.id)) continue;
    if (strain.isArchived) continue;
    if (strain.saveStatus === 'incomplete') continue;
    if (!hasValidWheelThc(strain.thcPercent)) continue;
    if (!canLoadIntoCalculator(strain)) continue;
    seen.add(strain.id);
    out.push(strain);
  }
  return out;
}

/**
 * Label-level balancing candidates (before mathematical blend check).
 * Excludes the primary strain id when provided.
 */
export function filterBalancingLabelEligible(
  strains: readonly WheelStrainCandidate[],
  primaryStrainId: string | null,
): WheelStrainCandidate[] {
  const seen = new Set<string>();
  const out: WheelStrainCandidate[] = [];
  for (const strain of strains) {
    if (seen.has(strain.id)) continue;
    if (!strain.isBalancingStrain) continue;
    if (strain.isArchived) continue;
    if (strain.saveStatus === 'incomplete') continue;
    if (!hasValidWheelThc(strain.thcPercent)) continue;
    if (primaryStrainId !== null && strain.id === primaryStrainId) continue;
    seen.add(strain.id);
    out.push(strain);
  }
  return out;
}
